import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Check, ArrowDown, Gift } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { scrollToId } from "../components/Nav";
import { GiftKit } from "../components/GiftKit";
import { Reveal, MaskLines } from "../components/Reveal";
import { useLang, WHATSAPP } from "../lib/i18n";
import { useProducts } from "../lib/content";

const UI = {
  it: { over: "Shop · Gioia Skincare", t1: "La cura di cabina,", t2: "a casa tua.", sub: "I prodotti Gioia Skincare, scelti e consigliati da Gioia. Chiedi informazioni su WhatsApp: ti risponderemo con disponibilità e consigli d'uso.", info: "Chiedi info", kit: "Componi un kit regalo", more: "Ingredienti e risultati", msg: (n) => `Ciao Gioia, vorrei informazioni sulla ${n} Gioia Skincare.`, img: "Immagine" },
  en: { over: "Shop · Gioia Skincare", t1: "Salon care,", t2: "at home.", sub: "Gioia Skincare products, chosen and recommended by Gioia. Ask us on WhatsApp: we'll reply with availability and tips for use.", info: "Ask for info", kit: "Build a gift kit", more: "Ingredients & results", msg: (n) => `Hi Gioia, I'd like some information about the Gioia Skincare ${n}.`, img: "Image" },
};

const InfoButton = ({ p, c, u, id }) => (
  <a href={`${WHATSAPP}?text=${encodeURIComponent(u.msg(c.name.charAt(0).toLowerCase() + c.name.slice(1)))}`} target="_blank" rel="noreferrer" data-testid={id}
    className="group inline-flex justify-center items-center gap-3 rounded-full bg-[#14110F] text-[#F6F2EC] px-8 py-4 text-[0.75rem] tracking-[0.2em] uppercase transition-colors duration-500 hover:bg-[#8A6A3A]">
    <MessageCircle className="w-4 h-4" /> {u.info}
  </a>
);

const Gallery = ({ p, c, u }) => {
  const imgs = [p.img, ...p.gallery];
  const [i, setI] = useState(0);
  return (
    <div>
      <div className={`relative overflow-hidden aspect-[4/5] bg-[#ECE5DA] transition-[border-radius] duration-700 ${i === 0 ? "rounded-t-[999px]" : "rounded-none"}`} data-testid={`product-main-image-${p.slug}`}>
        <motion.img key={imgs[i]} src={imgs[i]} alt={`${c.name} – ${u.img} ${i + 1}`} initial={{ opacity: 0.4, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
          className={`absolute inset-0 w-full h-full ${i === 0 ? "object-cover" : "object-contain p-3"}`} />
      </div>
      <div className="mt-3 grid grid-cols-4 gap-3">
        {imgs.map((src, k) => (
          <button key={src} type="button" onClick={() => setI(k)} data-testid={`product-thumb-${p.slug}-${k}`} aria-label={`${u.img} ${k + 1}`}
            className={`relative aspect-square overflow-hidden bg-[#ECE5DA] border transition-colors duration-300 ${i === k ? "border-[#8A6A3A]" : "border-transparent hover:border-[#ddd4c7]"}`}>
            <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover object-top" />
          </button>
        ))}
      </div>
    </div>
  );
};

const Product = ({ p, lang, u }) => {
  const c = p[lang];
  return (
    <article id={p.slug} data-testid={`product-${p.slug}`} className="space-y-24 sm:space-y-32 scroll-mt-28 pt-4">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-6"><Gallery p={p} c={c} u={u} /></div>
        <div className="lg:col-span-6 lg:pt-10">
          <p className="overline text-[#8A6A3A]">{c.line}</p>
          <h2 className="mt-5 font-serif-d font-light text-5xl sm:text-7xl leading-[0.95] text-[#14110F]" data-testid={`product-name-${p.slug}`}>{c.name}</h2>
          <p className="mt-6 font-serif-d italic text-2xl sm:text-3xl text-[#8A6A3A] leading-snug">{c.tagline}</p>
          <ul className="mt-8 flex flex-wrap gap-2">
            {c.tags.map((t) => <li key={t} className="text-[0.72rem] tracking-[0.12em] uppercase px-3 py-1.5 rounded-full border border-[#14110F]/20 text-[#14110F]">{t}</li>)}
          </ul>
          <ul className="mt-10 border-t border-[#ddd4c7]" data-testid={`product-benefits-${p.slug}`}>
            {c.benefits.map((b) => (
              <li key={b} className="flex gap-4 items-center py-4 border-b border-[#ddd4c7] text-[#14110F] font-light">
                <Check className="w-4 h-4 text-[#8A6A3A] shrink-0" strokeWidth={1.6} /> {b}
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <InfoButton p={p} c={c} u={u} id={`product-info-btn-${p.slug}`} />
            <a href={`#${p.slug}-ingredients`} data-testid={`product-more-link-${p.slug}`} className="inline-flex justify-center items-center gap-2 px-4 py-4 text-[0.72rem] tracking-[0.2em] uppercase text-[#14110F] underline underline-offset-8 decoration-[#8A6A3A] hover:text-[#8A6A3A] transition-colors duration-300">
              {u.more} <ArrowDown className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      <div id={`${p.slug}-ingredients`} className="bg-[#14110F] grain text-[#F6F2EC] -mx-5 sm:mx-0 px-6 sm:px-12 lg:px-16 py-16 sm:py-20 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Reveal><h3 className="font-serif-d font-light text-4xl sm:text-5xl leading-[1]">{c.ingredientsTitle}</h3></Reveal>
          <Reveal delay={0.05}><p className="mt-5 text-[#d9d0c4] font-light">{c.ingredientsSub}</p></Reveal>
        </div>
        <ol className="lg:col-span-8 border-t border-[#F6F2EC]/15" data-testid={`product-ingredients-${p.slug}`}>
          {c.ingredients.map(([n, d], i) => (
            <Reveal as="li" key={n} delay={i * 0.05} className="grid sm:grid-cols-12 gap-2 sm:gap-6 py-6 border-b border-[#F6F2EC]/15">
              <span className="sm:col-span-1 font-stencil tracking-[0.2em] text-[#C9A66B]">0{i + 1}</span>
              <h4 className="sm:col-span-4 font-serif-d text-2xl font-light">{n}</h4>
              <p className="sm:col-span-7 text-sm text-[#d9d0c4] font-light leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </ol>
      </div>

      <div>
        <Reveal><h3 className="font-serif-d font-light text-4xl sm:text-6xl text-[#14110F] leading-[1]">{c.resultsTitle}</h3></Reveal>
        <Reveal delay={0.05}><p className="mt-4 text-[#4f4841] font-light">{c.resultsSub}</p></Reveal>
        <div className={`mt-12 grid sm:grid-cols-2 ${c.results.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-px bg-[#ddd4c7] border border-[#ddd4c7]`} data-testid={`product-results-${p.slug}`}>
          {c.results.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.06} className="bg-[#F6F2EC] p-7 h-full">
              <span className="font-stencil tracking-[0.25em] text-[#8A6A3A]">0{i + 1}</span>
              <h4 className="mt-6 font-serif-d text-2xl font-light text-[#14110F]">{t}</h4>
              <p className="mt-3 text-sm text-[#4f4841] font-light leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="border border-[#ddd4c7] p-8 sm:p-14 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
        <p className="font-serif-d italic text-3xl sm:text-4xl text-[#14110F] max-w-2xl leading-snug" data-testid={`product-claim-${p.slug}`}>{c.claim}</p>
        <InfoButton p={p} c={c} u={u} id={`product-info-btn-bottom-${p.slug}`} />
      </div>
    </article>
  );
};

const Index = ({ lang, products }) => (
  <nav className="grid sm:grid-cols-3 gap-4" data-testid="shop-index">
    {products.map((p, i) => (
      <a key={p.slug} href={`#${p.slug}`} onClick={(e) => { e.preventDefault(); scrollToId(p.slug); }} data-testid={`shop-index-${p.slug}`}
        className="group flex sm:flex-col gap-4 items-center sm:items-start border border-[#ddd4c7] p-3 sm:p-4 transition-colors duration-300 hover:border-[#8A6A3A]">
        <div className="relative w-20 sm:w-full aspect-[4/5] sm:aspect-[4/3] shrink-0 overflow-hidden bg-[#ECE5DA]">
          <img src={p.img} alt={p[lang].name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        </div>
        <div>
          <span className="font-stencil tracking-[0.25em] text-[#8A6A3A] text-sm">0{i + 1}</span>
          <p className="mt-1 font-serif-d text-xl sm:text-2xl font-light text-[#14110F] leading-tight">{p[lang].name}</p>
        </div>
      </a>
    ))}
  </nav>
);

export default function Shop() {
  const { lang } = useLang();
  const { hash } = useLocation();
  const products = useProducts();
  const u = UI[lang] ?? UI.it;
  useEffect(() => {
    document.title = "Shop Gioia Skincare · Gioia Centro Estetico";
    if (!hash) { window.scrollTo(0, 0); return undefined; }
    const t = setTimeout(() => scrollToId(hash.slice(1)), 600);
    return () => clearTimeout(t);
  }, [hash]);
  return (
    <PageShell>
      <div className="max-w-[1280px] mx-auto space-y-20 sm:space-y-28" data-testid="shop-page">
        <header className="max-w-3xl">
          <p className="overline text-[#8A6A3A]">{u.over}</p>
          <h1 className="mt-6 font-serif-d font-light text-[3rem] sm:text-7xl leading-[0.95] text-[#14110F]">
            <MaskLines lines={[u.t1]} delay={0.1} />
            <MaskLines lines={[u.t2]} delay={0.2} lineClass="italic text-[#8A6A3A]" />
          </h1>
          <p className="mt-8 text-[#4f4841] font-light leading-relaxed max-w-xl">{u.sub}</p>
          <a href="#kit-regalo" onClick={(e) => { e.preventDefault(); scrollToId("kit-regalo"); }} data-testid="shop-kit-link"
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#8A6A3A] text-[#8A6A3A] px-6 py-3 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-300 hover:bg-[#8A6A3A] hover:text-[#F6F2EC]">
            <Gift className="w-4 h-4" /> {u.kit}
          </a>
        </header>
        <Index lang={lang} products={products} />
        {products.map((p) => <Product key={p.slug} p={p} lang={lang} u={u} />)}
        <GiftKit lang={lang} products={products} />
      </div>
    </PageShell>
  );
}
